import { useEffect, useMemo, useState } from 'react';
import {
	fileViewerPluginName,
	isWatermarkResponePresent,
} from '@sharefiledev/fileviewer-pilet-sdk';
import { LinkSettings } from '@sharefiledev/sharing-pilet-sdk/api';
import { useAppContext } from '../../../AppContext';
import { usePluginContext } from '../../../PermissionsContext/usePluginContext';
import {
	emitWatermarkRequest,
	listenForWatermarkResponse,
	WatermarkConfig,
} from '../../ResourceContext/WatermarkEvents';
import { isWatermarkingAvailableForResources } from '../utils/common-utils';

export interface UseWatermarkingStateProps {
	accountLevelLinkSettings: LinkSettings | undefined;
}

export const useWatermarkingState = ({
	accountLevelLinkSettings,
}: UseWatermarkingStateProps) => {
	const { piletApi } = useAppContext();
	const { selectedResources } = usePluginContext();
	const [isWatermarkingAvailable, setIsWatermarkingAvailable] = useState<boolean>(false);
	const [watermarkedResponse, setWatermarkedResponse] = useState<WatermarkConfig>({
		success: false,
		transformedRids: [],
		labels: [],
		pluginName: '',
	});

	const rids = selectedResources.map(r => r.rid);
	const ridsKey = useMemo(() => JSON.stringify(rids.sort()), [rids]);

	useEffect(() => {
		if (!accountLevelLinkSettings || !isWatermarkResponePresent(watermarkedResponse)) {
			setIsWatermarkingAvailable(false);
			return;
		}

		const available = isWatermarkingAvailableForResources(
			selectedResources,
			accountLevelLinkSettings,
			watermarkedResponse?.pluginName
		);

		setIsWatermarkingAvailable(available);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ridsKey, accountLevelLinkSettings, watermarkedResponse]);

	useEffect(() => {
		const initialAvailability =
			accountLevelLinkSettings &&
			isWatermarkingAvailableForResources(
				selectedResources,
				accountLevelLinkSettings,
				fileViewerPluginName
			);

		if (initialAvailability) {
			emitWatermarkRequest(piletApi, rids);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [piletApi, ridsKey, accountLevelLinkSettings]);

	useEffect(() => {
		const cleanup = listenForWatermarkResponse(piletApi, response => {
			setWatermarkedResponse(response);
		});

		return cleanup;
	}, [piletApi]);

	return {
		isWatermarkingAvailable,
		watermarkedResponse,
	};
};
